import React from "react";
import { ShieldAlert, Info } from "lucide-react";
import { Panel, TitledPanel, EmptyState } from "@/portal/ui/Layout";
import { Badge, StatusBadge, RISK_LEVEL, RISK_STATUS } from "@/portal/ui/Badge";
import { fmtDateHuman } from "@/portal/format";

const SCORE = { low: 1, medium: 2, high: 3 };

export default function RiskTab({ portal }) {
  const risks = [...(portal?.risks || [])].sort((a, b) => (SCORE[b.probability] * SCORE[b.impact]) - (SCORE[a.probability] * SCORE[a.impact]));
  const health = portal?.health?.risk;
  if (risks.length === 0) return <EmptyState icon={ShieldAlert} title="Registrul de riscuri nu a fost încă publicat" description="Echipa Brisk publică aici riscurile identificate, nivelul lor și măsurile de atenuare." testid="risks-empty" />;
  const open = risks.filter((r) => r.status === "open").length;
  const monitoring = risks.filter((r) => r.status === "monitoring").length;
  const high = risks.filter((r) => r.status !== "closed" && SCORE[r.probability] * SCORE[r.impact] >= 6).length;

  return (
    <div className="grid grid-cols-1 xl:grid-cols-[1fr_300px] gap-5 items-start">
      <div>
        <div className="grid grid-cols-3 gap-4 mb-5" data-testid="risk-kpis">
          <Kpi label="Riscuri deschise" value={open} tone={open ? "text-cp-danger" : ""} />
          <Kpi label="În monitorizare" value={monitoring} tone={monitoring ? "text-cp-warning" : ""} />
          <Kpi label="Nivel ridicat" value={high} tone={high ? "text-cp-danger" : ""} />
        </div>
        <Panel padded={false} className="overflow-x-auto" data-testid="risks-table">
          <table className="cp-table min-w-[760px]">
            <thead><tr><th>Risc</th><th>Probabilitate</th><th>Impact</th><th>Măsuri de atenuare</th><th>Responsabil</th><th>Status</th></tr></thead>
            <tbody>
              {risks.map((r) => (
                <tr key={r.id} data-testid={`risk-${r.id}`}>
                  <td className="max-w-[280px]"><div className="text-[14px] font-medium text-cp-text">{r.title}</div>{r.description && <div className="cp-meta mt-0.5">{r.description}</div>}{r.due && <div className="cp-meta mt-0.5">Termen: {fmtDateHuman(r.due)}</div>}</td>
                  <td><Badge tone={RISK_LEVEL[r.probability]?.tone || "neutral"} size="sm" dot>{RISK_LEVEL[r.probability]?.label || r.probability}</Badge></td>
                  <td><Badge tone={RISK_LEVEL[r.impact]?.tone || "neutral"} size="sm" dot>{RISK_LEVEL[r.impact]?.label || r.impact}</Badge></td>
                  <td className="max-w-[280px]">{r.mitigation || "—"}</td>
                  <td className="whitespace-nowrap">{r.owner || "—"}</td>
                  <td><StatusBadge map={RISK_STATUS} value={r.status} fallback="open" size="sm" /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </Panel>
      </div>
      <div className="space-y-4">
        {health && (
          <TitledPanel title="Evaluarea PM" testid="risk-health">
            <StatusBadge map={{ "On Track": { label: "Risc controlat", tone: "success" }, "Attention Required": { label: "Necesită atenție", tone: "warning" }, "Critical": { label: "Critic", tone: "danger" } }} value={health.level} />
            <p className="cp-body-sm mt-3">{health.note || "Riscurile sunt monitorizate săptămânal de echipa de proiect."}</p>
          </TitledPanel>
        )}
        <div className="cp-panel-soft p-4 flex gap-3 text-[13px] text-cp-text-2" data-testid="risk-legend">
          <Info className="h-4 w-4 text-cp-accent shrink-0 mt-0.5" aria-hidden="true" />
          <div><p className="font-semibold text-cp-text mb-1">Cum citești registrul</p><p>Riscurile sunt ordonate după severitate (probabilitate × impact). „Monitorizat” înseamnă că măsurile sunt active, „Atenuat” că impactul a fost redus.</p></div>
        </div>
      </div>
    </div>
  );
}

function Kpi({ label, value, tone }) {
  return <div className="cp-panel px-4 py-3.5"><div className="text-[12.5px] text-cp-text-2">{label}</div><div className={`cp-metric mt-1 ${tone}`}>{String(value).padStart(2, "0")}</div></div>;
}
